import { Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import {
  API,
  CampaignWithPreviewDTO,
  ds_cm_Campaign_retrieveAllFrontendEnhanced_order,
  Order,
  Pagination,
  TimingStatus,
} from '../../services/api/api';
import { ErrorService } from '../../services/error.service';
import { ModalService } from '../../services/gui/modal/modal.service';
import { UtilService } from '../../services/util.service';
import { ModalNavigationComponent } from 'src/app/services/gui/modal/modal-navigation-component';
import { ModalNavigationService } from 'src/app/services/gui/modal/modal-navigation.service';
import { TranslateService } from '@ngx-translate/core';
import { finalize, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { StringTransformationService } from '../../services/transformation-helper/string-transformation.service';

@Component({
  selector: 'sb-campaign-ordering',
  templateUrl: './sb-campaign-ordering.component.html',
  styleUrls: ['./sb-campaign-ordering.component.scss'],
  standalone: false,
})
export class SbCampaignOrderingComponent implements OnInit, ModalNavigationComponent {
  private readonly stringTransformation = inject(StringTransformationService);

  SHOWFILTERPARAMS: boolean = false;

  isInModalContext: boolean;

  @Input() campaignToRearrange: CampaignWithPreviewDTO;

  campaigns: CampaignWithPreviewDTO[];
  resultCount: number;
  selectedCompanyId: number;
  debounceTimer: ReturnType<typeof setTimeout> = null;

  constructor(
    private readonly api: API,
    public readonly modal: ModalService,
    public readonly modalNavigation: ModalNavigationService,
    public readonly errorHandler: ErrorService,
    public readonly translate: TranslateService,
    private readonly destroyRef: DestroyRef
  ) {}

  ghosts = [];
  maxSize: number;
  paginationString: string;

  boundaryLinks: boolean = true;
  collectionSize: number;
  pagination: BehaviorSubject<Pagination>;
  _page: number;
  search: string;
  loading = true;

  timingPlanned: boolean = true;
  timingActive: boolean = true;
  timingEnded: boolean = false;
  timingUnknown: boolean = true;

  sizes: number[] = [8, 12, 16, 20, 24];

  paginationInitialized: boolean = false;

  get page(): number {
    return this._page;
  }

  set page(val: number) {
    this._page = val;
    this.pagination.value.page = val;
  }

  pageChange($event: number) {
    this.page = $event;
    this.refreshFromServer();
  }

  ngOnInit() {
    if (this.isInModalContext) {
      const data: CampaignArrangementModalParams = this.modalNavigation.getCurrentNavigation()?.data;
      if (data) {
        this.campaignToRearrange = data.campaign;
        this.selectedCompanyId = data.company;

        // values from modalData or default value see variable declaration
        this.timingPlanned = data.timingPlanned ?? true;
        this.timingActive = data.timingActive ?? true;
        this.timingEnded = data.timingEnded ?? false;
        this.timingUnknown = data.timingUnknown ?? true;
      }
    }
    this.pagination = new BehaviorSubject<Pagination>({ page: 1, count: 8, pages: -1, resultcount: -1 });
    this.page = 1;
    this.pagination.subscribe({
      next: (e: Pagination) => {
        this.collectionSize = e.resultcount;
        this.maxSize = e.count;
        this.resultCount = e.resultcount;
      },
      error: error => {
        this.errorHandler.errorHandler(error);
      },
    });
    this.refreshFromServer();
  }

  getTimingSelections(): TimingStatus[] {
    const ret: TimingStatus[] = [];
    if (this.timingPlanned) {
      ret.push(TimingStatus.Future);
    }
    if (this.timingActive) {
      ret.push(TimingStatus.Present);
    }
    if (this.timingEnded) {
      ret.push(TimingStatus.Past);
    }
    if (this.timingUnknown) {
      ret.push(TimingStatus.Unknown);
    }
    return ret.length > 0 ? ret : null;
  }

  requestDebounced() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = setTimeout(() => {
      this.refreshFromServer();
    }, 500);
  }

  refreshFromServer() {
    this.loading = true;
    if (!this.campaigns) {
      this.ghosts = new Array(this.maxSize); // Mock Ghost items
    }

    this.api
      .ds_cm_Campaign_retrieveAllFrontendEnhanced(
        {
          search: this.stringTransformation.encodeSearchQueryParam(false, this.search),
          timingStatus: this.getTimingSelections(),
          company: this.selectedCompanyId ? [this.selectedCompanyId] : null,
          order: { by: ds_cm_Campaign_retrieveAllFrontendEnhanced_order.CLIENT_ORDER_NUM, direction: Order.asc },
        },
        this.pagination
      )
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        take(1),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe({
        next: data => {
          if (!this.campaigns) {
            this.ghosts = [];
          }
          this.campaigns = data;
          this.paginationString = this.stringTransformation.getPaginationString(
            this.page,
            this.maxSize,
            this.campaigns.length,
            this.resultCount,
            'campaigns'
          );
          this.paginationInitialized = true;
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  searchCampaign(searchString: string) {
    this.search = searchString;
    this.requestDebounced();
  }

  putAfter(campaign: CampaignWithPreviewDTO) {
    this.loading = true;
    this.api
      .shared_ClientOrder_putCampaignAfter(this.campaignToRearrange.id, campaign.id)
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (success: boolean) => {
          this.modalNavigation.result(success);
        },
        error: error => {
          this.modalNavigation.result(false);
          this.errorHandler.errorHandler(error);
        },
      });
  }

  putBefore(campaign: CampaignWithPreviewDTO) {
    this.loading = true;
    this.api
      .shared_ClientOrder_putCampaignBefore(this.campaignToRearrange.id, campaign.id)
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (success: boolean) => {
          this.modalNavigation.result(success);
        },
        error: error => {
          this.modalNavigation.result(false);
          this.errorHandler.errorHandler(error);
        },
      });
  }

  isPrevious(i: number) {
    if (i > 0 && i < this.campaigns?.length && i - 1 > 0) {
      return this.campaigns[i - 1]?.id === this.campaignToRearrange?.id;
    }

    return false;
  }
}

export interface CampaignArrangementModalParams {
  campaign: CampaignWithPreviewDTO;
  timingPlanned?: boolean;
  timingActive?: boolean;
  timingEnded?: boolean;
  timingUnknown?: boolean;
  company?: number;
}
